// connection.js
//
// One place that owns Firestore connection recovery for the PWA. On mobile, a
// PWA suspended by the OS comes back with a DEAD Firestore connection that the
// SDK-level nudge (disableNetwork→enableNetwork) does not reliably revive — only
// a full page reload does. Users just want to see fresh data quickly on return,
// so the policy is:
//   • Long background (phone slept) → do a fast, guarded FULL RELOAD on return.
//     A fresh load reconnects cleanly and paints cached data instantly, so it's
//     quick — far better than showing a stale reading behind a spinner and making
//     the user hunt for a reload button.
//   • Short background (quick app switch) → the connection probably survived, so
//     just nudge it (reconnect) and let one-shot pages refetch via 'app:resume'.

import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { db } from './firebase';

export const APP_RESUME_EVENT = 'app:resume';

let installed = false;
let hiddenAt = 0;
let recovering = false;
let lastRecoverAt = 0;

// A quick tab flick shouldn't trigger recovery; a real background should.
const MIN_BACKGROUND_MS = 8 * 1000;
// Backgrounded at least this long on return → reload rather than nudge (the
// connection is almost certainly dead after a suspend of this length).
const RELOAD_AFTER_HIDDEN_MS = 15 * 1000;
const RECOVER_DEBOUNCE_MS = 3 * 1000;

// Drop + re-establish the Firestore connection. Safe to call on any suspected
// stall (debounced). Exported so a read that times out can actively un-stick the
// connection instead of passively retrying against a dead one.
export async function reconnect() {
  const now = Date.now();
  if (recovering || now - lastRecoverAt < RECOVER_DEBOUNCE_MS) return;
  recovering = true;
  lastRecoverAt = now;
  try {
    await disableNetwork(db);
    await enableNetwork(db);
  } catch (e) {
    console.warn('Firestore reconnect nudge failed:', e);
  } finally {
    recovering = false;
  }
}

// A full page reload reliably re-establishes a Firestore connection that died
// while the phone slept (the SDK-level nudge above is not always enough). When a
// page's load is stuck past its watchdog, call this to self-heal WITHOUT the user
// tapping a button. Guarded so it can never loop: skipped when offline, and at
// most once per AUTORELOAD_MIN_GAP_MS (tracked in sessionStorage, which survives
// the reload) — so if a reload doesn't fix it, we stop and let the manual button
// show instead of reloading forever. Returns true if a reload was started.
const AUTORELOAD_KEY = 'soko.lastAutoReload';
// Loop guard only: long enough that a stuck post-reload load (watchdog ~8s) does
// NOT reload again (it shows the button instead), short enough that a genuine
// later resume can reload. Legitimate resume-reloads are ≥ RELOAD_AFTER_HIDDEN_MS
// apart, so this never blocks them.
const AUTORELOAD_MIN_GAP_MS = 12 * 1000;

export function attemptAutoReload() {
  try {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    const last = Number(sessionStorage.getItem(AUTORELOAD_KEY) || 0);
    if (Date.now() - last < AUTORELOAD_MIN_GAP_MS) return false; // already tried recently → let the button show
    sessionStorage.setItem(AUTORELOAD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

function onResume(hiddenMs) {
  // Long background → reload for a clean, fast reconnect. attemptAutoReload is
  // guarded (skips offline, won't loop); if it starts a reload the page is gone.
  if (hiddenMs >= RELOAD_AFTER_HIDDEN_MS && attemptAutoReload()) return;
  // Short background (or reload suppressed/offline) → nudge + let pages refetch.
  reconnect().finally(() => {
    try { window.dispatchEvent(new Event(APP_RESUME_EVENT)); } catch { /* no-op */ }
  });
}

export function installConnectionRecovery() {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    const hiddenMs = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = 0;
    if (hiddenMs > MIN_BACKGROUND_MS) onResume(hiddenMs);
  });

  // iOS PWAs don't always fire visibilitychange cleanly on relaunch; pageshow
  // (bfcache restore) is a resume too — treat it as a long one (reload). Network
  // coming back is a nudge, not necessarily a reload.
  window.addEventListener('pageshow', (e) => { if (e.persisted) onResume(RELOAD_AFTER_HIDDEN_MS); });
  window.addEventListener('online', () => onResume(0));
}
